import Container from "@client/content/Containers/container";
import { COLUMN_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getContainerProps, getRootContainerProps } from "@client/content/lib/helper";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { Loading } from "@client/controls/Loading/component";
import { getUnwrapResponse, parseQueryString } from "@client/lib/api";
import { getUserDisplayName } from "@client/lib/misc";
import Countdown from "@common/controls/Countdown/component";
import { useI18n } from "@common/hooks/i18n";
import { updateItemBy } from "@common/lib/array";
import { formatMoneyDisplay } from "@common/lib/format";
import { usePromise } from "@common/lib/hooks";
import { i18nFormat } from "@common/lib/i18n";
import { uuid } from "@common/lib/misc";
import * as React from "react";
import { ModuleButton, ModuleSecondaryButton } from "../Button";
import api from "./api";
import { getOperationLabel } from "./lib";
import { type MemberVerification } from "./types";

export type MapProps = RootContainerProps & {
  apiInfo: ModuleApiInfo;
  isEditMode: boolean;
};

type QS = {
  otp?: string;
  s?: string;
};
const Content = (props: MapProps) => {
  const i18n = useI18n();

  const qs = React.useMemo(() => parseQueryString<QS>(), []);

  const [refreshKey, setRefreshKey] = React.useState(uuid());
  const [isUpdating, setIsUpdating] = React.useState(false);

  const [verifications, isLoading, setVerifications] = usePromise<MemberVerification.Api.Verification[]>({
    fn: (...args) => {
      if (props.isEditMode) {
        return Promise.resolve([
          {
            accountTransactionId: "",
            amount: 0,
            beneficiaryCurrencyCode: "USD",
            otpExpiryDate: new Date(new Date().valueOf() + 6000000).toISOString(),
            sourceUserFirstName: "Dummy",
            sourceUserLastName: "Dummy",
            status: "Pending",
            verificationType: "AccountTransactionWithdrawal"
          }
        ]);
      }

      if (!qs.otp || !qs.s) return Promise.resolve([]);

      return getUnwrapResponse(api.init)(...args);
    },
    args: [props.apiInfo, qs.otp, qs.s],
    shouldLoad: true
  });

  const onButtonClick = React.useCallback(
    async (id: string, isSucceeded: boolean) => {
      try {
        setIsUpdating(true);

        const res = await getUnwrapResponse(api.verify)(props.apiInfo, id, {
          isSucceeded,
          otp: qs.otp,
          scheduleId: qs.s
        });

        setVerifications((items) =>
          updateItemBy(
            items,
            (x) => x.accountTransactionId === id,
            (x) => ({ ...x, status: res?.accountTransactionStatusKey ?? "Declined" })
          )
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [props.apiInfo, qs.otp, qs.s]
  );

  React.useEffect(() => {
    if (props.isEditMode || !verifications?.length || verifications.every((x) => x.status !== "Pending")) return;

    let timeout;
    let isCancelled = false;
    const poll = async () => {
      const res = await getUnwrapResponse(api.init)(props.apiInfo, qs.otp, qs.s);

      if (isCancelled) return;

      if (verifications.length !== res.length) {
        setVerifications((items) =>
          updateItemBy(
            items,
            (x) => {
              const hasExpired = new Date(x.otpExpiryDate).getTime() < new Date().getTime();
              return (
                x.status === "Pending" &&
                !hasExpired &&
                !res.find((y) => y.accountTransactionId === x.accountTransactionId)
              );
            },
            (x) => ({ ...x, status: "Declined" })
          )
        );
      } else {
        timeout = setTimeout(poll, 5000);
      }
    };

    poll();

    return () => {
      isCancelled = true;
      clearTimeout(timeout);
    };
  }, [verifications, props.isEditMode]);

  const getVerification = React.useCallback(
    (data: MemberVerification.Verification) => {
      const hasExpired = new Date(data.otpExpiryDate).getTime() < new Date().getTime();
      if (data.status === "Complete" || data.status === "Declined" || hasExpired) {
        return (
          <div key={data.accountTransactionId} className="fr jcc">
            {i18nFormat(
              data.status === "Complete"
                ? i18n.infoOperationApproved
                : data.status === "Declined"
                ? i18n.infoOperationCanceled
                : i18n.infoOperationExpired,
              ["OPERATION", getOperationLabel(i18n, data.verificationType)]
            )}
          </div>
        );
      }

      return (
        <div key={data.accountTransactionId} className="fc fg">
          <h3>
            {i18nFormat(i18n.verifyOperation, [
              "OPERATION",
              getOperationLabel(i18n, data.verificationType).toLowerCase()
            ])}
          </h3>
          <div className="fc fg sm">
            <div className="fr jcsb">
              <span>{i18n.operator}:</span>
              <span>
                {getUserDisplayName({
                  userFirstName: data.sourceUserFirstName,
                  userLastName: data.sourceUserLastName
                })}
              </span>
            </div>
            <div className="fr jcsb">
              <span>{i18n.expiresIn}:</span>
              <span>
                <Countdown
                  date={data.otpExpiryDate}
                  onComplete={() => {
                    setRefreshKey(uuid());
                  }}
                />
              </span>
            </div>
            <div className="fr jcsb">
              <span>{i18n.amount}:</span>
              <span>{formatMoneyDisplay(data.amount, data.beneficiaryCurrencyCode)}</span>
            </div>
          </div>
          <div style={COLUMN_BUTTONS_STYLE}>
            <ModuleButton
              {...getRootContainerProps(props)}
              isDisabled={isUpdating}
              onClick={() => onButtonClick(data.accountTransactionId, true)}
            />
            <ModuleSecondaryButton
              {...getRootContainerProps(props)}
              isDisabled={isUpdating}
              onClick={() => onButtonClick(data.accountTransactionId, false)}
            />
          </div>
        </div>
      );
    },
    [onButtonClick, isUpdating, refreshKey]
  );

  if (isLoading) return <Loading />;

  if (verifications?.length) return <div className="fc fg">{verifications.map((x) => getVerification(x))}</div>;

  return <div className="fr jcc">{i18n.infoOperationInvalid}</div>;
};

export default (props: MapProps) => (
  <Container {...getContainerProps(props)}>
    <Content {...props} />
  </Container>
);
